import { useEffect, useRef, type RefObject } from 'react';
import { attachCanvasViewMemory, type ViewCy } from '../utils/canvasViewMemory';
import type { GraphCanvasRef } from '../components/GraphCanvas';

const POLL_MS = 250;
const POLL_MAX_MS = 15000;

/**
 * Remember the Workbench camera and the nodes the officer dragged, per tab
 * session, and put them back after the layout settles on a return to /workbench.
 * Only the first attach after the page mounted with a canvas already present
 * restores; later attaches (a new investigation) just record.
 */
export function useCanvasViewMemory(
    graphRef: RefObject<GraphCanvasRef | null>,
    hasCanvas: boolean,
    version: string,
): void {
    const versionRef = useRef(version);
    versionRef.current = version;
    // True only for the first attach, and only when the page mounted with elements.
    const restoreOnceRef = useRef<boolean | null>(null);
    if (restoreOnceRef.current === null) restoreOnceRef.current = hasCanvas;

    useEffect(() => {
        if (!hasCanvas) return;
        let detach: (() => void) | null = null;
        let poll: ReturnType<typeof setInterval> | null = null;
        const startedAt = Date.now();

        const tryAttach = (): boolean => {
            const cy = graphRef.current?.getCy?.() as unknown as ViewCy | null | undefined;
            if (!cy || (cy as any).destroyed?.()) return false;
            const restore = restoreOnceRef.current === true;
            restoreOnceRef.current = false;
            detach = attachCanvasViewMemory(cy, {
                getVersion: () => versionRef.current,
                restore,
                pin: (id) => graphRef.current?.setNodeManualLock(id, true),
            });
            return true;
        };

        if (!tryAttach()) {
            poll = setInterval(() => {
                if (tryAttach() || Date.now() - startedAt > POLL_MAX_MS) {
                    if (poll) clearInterval(poll);
                    poll = null;
                }
            }, POLL_MS);
        }
        return () => {
            if (poll) clearInterval(poll);
            detach?.();
        };
    }, [graphRef, hasCanvas]);
}
